import { parse } from "graphql";
import { SchemaClient } from "../../../common/GraphqlClient";
import {
  DynamoDbOperations,
  DynamoDbTables,
  SchemaClientKinds,
  Source,
  activeFilter,
} from "../../../common/Source";
import { EMPTY_VALUE } from "../../../Schema";
import { uniqueSortedLabels } from "./filterValues";

/**
 * One row of the category-mapping master data: the valid combination of category, model,
 * manufacturer and variant that an equipment may be.
 */
export type CategoryMapping = {
  equipmentCategory: string;
  equipmentModel: string;
  manufacturer: string;
  variant: string;
};

/**
 * Equipment category mappings.
 *
 * Declared for both clients. They started out feeding grid set-filter options alone, which
 * exist only where a grid is rendered; they now also back the rule that a manufacturer, model
 * and variant have to be valid *for their category*, and that rule has to hold on every write
 * path — including the two that read master data straight from DynamoDB.
 */
const listCategoryMappings: Source<CategoryMapping[]> = {
  dynamodb: {
    // A scan, for the same reason as the linked instances: the table is keyed by the mapping
    // itself with no index to narrow on, and every row is a candidate.
    operation: DynamoDbOperations.SCAN,
    table: DynamoDbTables.CATEGORY_MAPPINGS,
    filter: activeFilter,
  },
  graphql: parse(`
    query listIRCategoryMappings($nextToken: String) {
      listIRCategoryMappings(
        limit: 1000,
        nextToken: $nextToken,
        filter: { isActive: { eq: true } }
      ) {
        nextToken
        items {
          equipmentCategory
          equipmentModel
          manufacturer
          variant
        }
      }
    }
  `),
};

/**
 * Cluster category mappings, whose columns carry the cluster-specific names. Aliased to the
 * equipment names so both lists share {@link CategoryMapping}.
 */
const listClusterCategoryMappings: Source<CategoryMapping[]> = {
  graphql: parse(`
    query listIRClusterCategoryMappings($nextToken: String) {
      listIRClusterCategoryMappings(
        limit: 1000,
        nextToken: $nextToken,
        filter: { isActive: { eq: true } }
      ) {
        nextToken
        items {
          equipmentCategory: clusterCategory
          equipmentModel: clusterModel
          variant
        }
      }
    }
  `),
};

/**
 * Reads the equipment category mappings.
 *
 * An empty list still cannot cause data loss: these values back a grid filter and a validation
 * rule, and neither reaches `DataManager`, so nothing is coerced away when the read fails. The
 * rule that reads them treats an empty list as "the source is unavailable" rather than "every
 * tuple is wrong" — refusing a whole file because a master-data table was briefly unreachable
 * would be worse than the invalid rows it would catch.
 *
 * That reasoning would stop holding the moment this source backed a `data` aspect, where an
 * empty option list nulls the field on every record written.
 *
 * @param client - Read client for the master data.
 * @returns The mappings, or an empty list.
 */
export const getCategoryMappings = async (client: SchemaClient): Promise<CategoryMapping[]> => {
  const mappings = await client.queryList(listCategoryMappings);
  return mappings === EMPTY_VALUE ? [] : mappings ?? [];
};

/**
 * Reads the cluster category mappings. GraphQL only, for the same reason as
 * {@link getCategoryMappings}.
 *
 * @param client - Read client for the master data.
 * @returns The mappings, or an empty list.
 */
export const getClusterCategoryMappings = async (
  client: SchemaClient
): Promise<CategoryMapping[]> => {
  if (client.kind !== SchemaClientKinds.GRAPHQL) {
    return [];
  }
  const mappings = await client.queryList(listClusterCategoryMappings);
  return mappings === EMPTY_VALUE ? [] : mappings ?? [];
};

/**
 * Manufacturer filter options.
 *
 * Equipment mappings only — clusters carry no manufacturer, and the app's own list is built
 * from the equipment mappings alone.
 *
 * @param client - Read client for the master data.
 * @returns Manufacturer names, deduplicated and sorted.
 */
export const getManufacturers = async (client: SchemaClient): Promise<string[]> =>
  uniqueSortedLabels(
    (await getCategoryMappings(client)).map((mapping) => mapping.manufacturer)
  );

/**
 * Variant filter options. Equipment mappings only, matching the app's list.
 *
 * @param client - Read client for the master data.
 * @returns Variant names, deduplicated and sorted.
 */
export const getVariants = async (client: SchemaClient): Promise<string[]> =>
  uniqueSortedLabels((await getCategoryMappings(client)).map((mapping) => mapping.variant));

/**
 * Equipment category filter options.
 *
 * Merges equipment and cluster mappings: the grid lists both kinds of row, so its filter has
 * to offer both sets of categories.
 *
 * @param client - Read client for the master data.
 * @returns Category names, deduplicated and sorted.
 */
export const getEquipmentCategories = async (client: SchemaClient): Promise<string[]> => {
  const [equipment, clusters] = await Promise.all([
    getCategoryMappings(client),
    getClusterCategoryMappings(client),
  ]);
  return uniqueSortedLabels(
    [...equipment, ...clusters].map((mapping) => mapping.equipmentCategory)
  );
};

/**
 * Equipment model filter options. Merges equipment and cluster mappings, as
 * {@link getEquipmentCategories} does.
 *
 * @param client - Read client for the master data.
 * @returns Model names, deduplicated and sorted.
 */
export const getEquipmentModels = async (client: SchemaClient): Promise<string[]> => {
  const [equipment, clusters] = await Promise.all([
    getCategoryMappings(client),
    getClusterCategoryMappings(client),
  ]);
  return uniqueSortedLabels([...equipment, ...clusters].map((mapping) => mapping.equipmentModel));
};
